/**
 * Wallet API Client
 *
 * 지갑 이체 및 내역 조회 API
 *
 * @see http://localhost:4333/v1/wallet (Development)
 */

import axios from 'axios';
import type {
  TransferRequest,
  ReverseTransferRequest,
  TransferResponse,
  TransferHistoryResponse,
  GetTransferHistoryParams,
  TradingWalletsResponse,
} from '@/types/wallet';

// API Base URL (환경변수)
const API_BASE_URL = import.meta.env.VITE_APP_NEW_API_URL || 'http://localhost:4333/v1';
const WALLET_BASE = `${API_BASE_URL}/wallet`;

/**
 * Axios 인스턴스 생성
 */
const apiClient = axios.create({
  baseURL: WALLET_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request Interceptor: JWT 토큰 자동 추가
 *
 * ⚠️ IMPORTANT: Zustand persist store에서 토큰 읽기
 * localStorage 'token-storage' 키에서 accessToken 추출
 */
apiClient.interceptors.request.use(
  (config) => {
    try {
      // Zustand persist store에서 토큰 가져오기
      const storageData = localStorage.getItem('token-storage');
      if (storageData) {
        const { state } = JSON.parse(storageData);
        const token = state?.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.error('[Wallet API] Failed to get token:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response Interceptor: 에러 핸들링
 */
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // 인증 실패 시 로그인 페이지로 리다이렉트
      console.error('[Wallet API] Unauthorized - redirecting to login');
      // window.location.href = '/login';
    }

    // 에러 메시지 구조화
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
    return Promise.reject({
      status: error.response?.status,
      message: errorMessage,
      data: error.response?.data,
    });
  }
);

// ==========================================
// Wallet Transfer API
// ==========================================

/**
 * 현물 → 봇/카피 지갑 이체
 */
export const transferToTradingWallet = async (
  params: TransferRequest
): Promise<TransferResponse> => {
  const response = await apiClient.post<any, TransferResponse>('/transfer', params);
  return response;
};

/**
 * 봇/카피 → 현물 지갑 이체
 */
export const transferFromTradingWallet = async (
  params: ReverseTransferRequest
): Promise<TransferResponse> => {
  const response = await apiClient.post<any, TransferResponse>('/transfer/reverse', params);
  return response;
};

/**
 * 이체 내역 조회
 */
export const getTransferHistory = async (
  params?: GetTransferHistoryParams
): Promise<TransferHistoryResponse> => {
  const response = await apiClient.get<any, TransferHistoryResponse>('/transfer/history', {
    params,
  });
  return response;
};

// ==========================================
// Wallet Balance API
// ==========================================

/**
 * 봇/카피 지갑 잔액 조회
 */
export const getTradingWallets = async (): Promise<TradingWalletsResponse> => {
  const response = await apiClient.get<any, TradingWalletsResponse>('/balances');
  return response;
};

// ==========================================
// 카피트레이딩 전용 API (WalletTransferModal에서 사용)
// ==========================================

/**
 * 카피트레이딩 지갑 전송 API
 */
export const walletTransferApi = {
  /**
   * 현물 → 카피트레이딩 지갑 전송
   */
  async transferToCopy(amount: number): Promise<TransferResponse> {
    const response = await apiClient.post<any, TransferResponse>('/transfer', {
      amount,
      toType: 'COPY',
      // fromType은 백엔드에서 기본값 'SPOT' 사용
    });
    return response;
  },

  /**
   * 카피트레이딩 → 현물 지갑 전송 (역방향)
   */
  async transferToSpot(amount: number): Promise<TransferResponse> {
    const response = await apiClient.post<any, TransferResponse>('/transfer/reverse', {
      amount,
      fromType: 'COPY',
      // toType은 백엔드에서 기본값 'SPOT' 사용
    });
    return response;
  },

  /**
   * 지갑 잔액 조회 (현물 + 카피)
   */
  async getBalances(): Promise<{ spot: number; copy: number }> {
    try {
      // /v1/wallet/balances API에서 현물 + 카피 지갑 조회
      const response = await getTradingWallets();
      
      const spotBalance = response.data?.spot 
        ? parseFloat(response.data.spot.balance) 
        : 0;
      const copyBalance = response.data?.copy 
        ? parseFloat(response.data.copy.balance) 
        : 0;

      return {
        spot: spotBalance,
        copy: copyBalance,
      };
    } catch (error) {
      console.error('[walletTransferApi] getBalances failed:', error);
      return { spot: 0, copy: 0 };
    }
  },
};

console.log(`[Wallet API] Using API Base URL: ${WALLET_BASE}`);
